import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getCurrentUser } from "../lib/auth";

export default function SetupPage() {
  const navigate = useNavigate();
  const user = getCurrentUser<{ sub: string; username: string; email: string }>();
  const [username, setUsername] = useState(user?.username ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Availability check
  const [availability, setAvailability] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (username.length < 3) {
      setAvailability("idle");
      return;
    }
    setAvailability("checking");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get<{ available: boolean }>(`/api/users/check?username=${encodeURIComponent(username)}`);
        setAvailability(res.available ? "available" : "taken");
      } catch {
        setAvailability("idle");
      }
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [username]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (availability === "taken") return;
    setSaving(true);
    setError(null);
    try {
      await api.patch("/api/me", { username });
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const inputBorder =
    availability === "available" ? "border-green-400 focus:ring-green-500" :
    availability === "taken"     ? "border-red-400 focus:ring-red-500" :
    "border-gray-300 dark:border-gray-700 focus:ring-blue-500";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Choose your username</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This will be used in your space URLs: <span className="font-mono">wiki.0xkaz.com/<strong>username</strong>/my-space</span>
          </p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                  setError(null);
                }}
                required
                minLength={3}
                maxLength={30}
                placeholder="your-username"
                className={`w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 font-mono transition-colors ${inputBorder}`}
              />
              <div className="mt-1 h-4">
                {availability === "checking" && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Checking availability...</p>
                )}
                {availability === "available" && (
                  <p className="text-xs text-green-600 dark:text-green-400">✓ Available</p>
                )}
                {availability === "taken" && (
                  <p className="text-xs text-red-600 dark:text-red-400">✗ Username already taken</p>
                )}
                {availability === "idle" && username.length > 0 && username.length < 3 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Lowercase letters, numbers, and hyphens only. 3–30 characters.</p>
                )}
              </div>
            </div>
            {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={saving || username.length < 3 || availability === "taken" || availability === "checking"}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
            >
              {saving ? "Saving..." : "Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
